/**
 * search-rank.js
 * Accent-insensitive text matching and result ranking.
 * Extend sortByScore fields array to add new searchable properties.
 */

// U+0300–U+036F: Unicode combining diacritical marks
const DIACRITICS_RE = /[̀-ͯ]/g;

export function normalizeText(text) {
  if (!text) return "";
  return String(text)
    .normalize("NFD")
    .replace(DIACRITICS_RE, "")
    .toLowerCase()
    .trim();
}

/**
 * Checks if all space-separated words in query match word-initials in text.
 * e.g. "jn pz" matches "Juan Pérez", "mr grc" matches "María García"
 * Requires at least 2 query words to activate (avoids false positives on single chars).
 */
function fuzzyWordInitials(text, query) {
  const tNorm = normalizeText(text);
  const qNorm = normalizeText(query);
  const qWords = qNorm.split(/\s+/).filter(Boolean);
  if (qWords.length < 2) return false;

  const tWords = tNorm.split(/\s+/).filter(Boolean);
  return qWords.every((qw) => tWords.some((tw) => tw.startsWith(qw)));
}

/**
 * Returns a score 0–3 (higher = better match):
 *   3 = exact
 *   2 = starts with query
 *   1 = contains query
 *   0.5 = fuzzy word-initials match
 *   0 = no match
 */
export function scoreMatch(text, query) {
  const t = normalizeText(text);
  const q = normalizeText(query);
  if (!t || !q) return 0;
  if (t === q) return 3;
  if (t.startsWith(q)) return 2;
  if (t.includes(q)) return 1;
  if (fuzzyWordInitials(text, query)) return 0.5;
  return 0;
}

/**
 * Filter and sort items by best score across given fields.
 * Items with score 0 are excluded. Items with equal scores keep insertion order.
 */
export function sortByScore(items, query, fields) {
  if (!query?.trim() || !items?.length) return [];
  return items
    .map((item) => ({
      item,
      score: Math.max(0, ...fields.map((f) => scoreMatch(item[f], query))),
    }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((x) => x.item);
}
